import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Link, useNavigate, useSearchParams } from 'react-router';

import { appRoutes } from '@/app/router/routes';
import * as authRepository from '@/auth/data/authRepository';
import { useThemeColors } from '@/settings/handler/settingsStore';
import { PageChrome } from '@/ui/PageChrome';
import { PixelContainer } from '@/ui/pixel/PixelContainer';
import { PixelPasswordField } from '@/ui/pixel/PixelPasswordField';

export function ResetPasswordScreen() {
  const { t } = useTranslation();
  const colors = useThemeColors();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const token = params.get('token') ?? '';
  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [done, setDone] = useState(false);

  const onSubmit = async (): Promise<void> => {
    if (!token) {
      setError(t('authResetMissingToken'));
      return;
    }
    if (password.length < 8) {
      setError(t('authResetPasswordTooShort'));
      return;
    }
    if (password !== confirm) {
      setError(t('authResetPasswordMismatch'));
      return;
    }

    setBusy(true);
    setError(null);
    try {
      await authRepository.resetPassword(token, password);
      setDone(true);
    } catch {
      setError(t('authResetFailed'));
    } finally {
      setBusy(false);
    }
  };

  return (
    <PageChrome title={t('authResetPasswordTitle')}>
      <div className="flex flex-1 items-center justify-center px-7">
        <div className="w-full max-w-[420px]">
          <PixelContainer
            radius={3}
            pixelScale={2}
            padding="20px"
            backgroundColor={colors.surfaceContainerHighest}
            borderColor={colors.onSurface}
          >
            {done ? (
              <>
                <p className="leading-tight" style={{ fontSize: 16, color: colors.onSurface }}>
                  {t('authResetSuccess')}
                </p>
                <button
                  type="button"
                  className="mt-5 leading-tight underline"
                  style={{ fontSize: 16, color: colors.onSurface }}
                  onClick={() => navigate(appRoutes.auth)}
                >
                  {t('authSignIn')}
                </button>
              </>
            ) : (
              <>
                <p className="mb-5 leading-tight" style={{ fontSize: 16, color: colors.onSurface }}>
                  {t('authResetPasswordHint')}
                </p>
                <label className="mb-3 block">
                  <span className="mb-2 block leading-none" style={{ fontSize: 20, color: colors.onSurface }}>
                    {t('authPassword')}
                  </span>
                  <PixelPasswordField
                    value={password}
                    onChange={setPassword}
                    autoComplete="new-password"
                    disabled={busy}
                  />
                </label>
                <label className="mb-5 block">
                  <span className="mb-2 block leading-none" style={{ fontSize: 20, color: colors.onSurface }}>
                    {t('authResetConfirmPassword')}
                  </span>
                  <PixelPasswordField
                    value={confirm}
                    onChange={setConfirm}
                    autoComplete="new-password"
                    disabled={busy}
                  />
                </label>
                {error && (
                  <p className="mb-3 leading-tight" style={{ fontSize: 14, color: colors.onSurface, opacity: 0.8 }}>
                    {error}
                  </p>
                )}
                <button
                  type="button"
                  disabled={busy}
                  onClick={() => void onSubmit()}
                  className="w-full rounded border px-4 py-3 leading-tight disabled:opacity-60"
                  style={{
                    fontSize: 16,
                    backgroundColor: colors.surfaceContainerHigh,
                    color: colors.onSurface,
                    borderColor: colors.outline,
                  }}
                >
                  {busy ? t('authResetWorking') : t('authResetSubmit')}
                </button>
                <Link
                  to={appRoutes.auth}
                  className="mt-4 inline-block leading-tight underline"
                  style={{ fontSize: 14, color: colors.onSurface, opacity: 0.7 }}
                >
                  {t('authSignIn')}
                </Link>
              </>
            )}
          </PixelContainer>
        </div>
      </div>
    </PageChrome>
  );
}
